import React, { useEffect, useMemo } from 'react';
import { useAppDispatch } from '../../../store/store';
import { orderNavigationSlice, OrderTabs } from '../../../store/order/orderNavigation/orderNavigationSlice';
import { loadEscrowBalancesAsync } from '../../../store/order/escrow/escrowBalancesHeaderSlice';
import { RootStore } from '../../../store/types';
import { useKeyToSelect } from '../../../store/createKeyedDataAsyncSlice';
import { WireDepositsContainer } from './deposits/wireDeposits/WireDepositsContainer';
import { InternalTransfersContainer } from './disbursements/internalTransfers/InternalTransfersContainer';
import { EscrowWireDisbursementsContainer } from './disbursements/wireDisbursements/EscrowWireDisbursementsContainer';
import { UnrestrictedInternalTransfersContainer } from './disbursements/internalTransfers/unrestricted/UnrestrictedInternalTransfersContainer';
import { AchPushDisbursementsContainer } from './disbursements/achPushDisbursements/AchPushDisbursementsContainer';
import { getEscrowFileAndVoidUsersAction } from '../../../store/order/escrow/getEscrowFileAndVoidUsersAction';
import { CheckDepositsContainer } from './deposits/checkDeposits/CheckDepositsContainer';
import { Card, ErrorMessage, Grid } from 'ui-toolkit';
import { EscrowFileJournalEntriesContainer } from './escrowFilesJournalEntries/EscrowFileJournalEntriesContainer';
import { AchDepositsContainer } from './deposits/achPullDeposits/AchDepositsContainer';
import { ManualCheckDisbursementsTable } from './disbursements/manualCheckDisbursementsTable/ManualCheckDisbursementsTable';
import { useGetDetailedEscrowDataQuery } from 'API/order/escrow/EscrowBaseAPI';
import { convertToError } from 'API/BaseApi';
import { IEscrowWireDisbursementsData } from '../../../types/order/escrow/IEscrowWireDisbursementsData';
import { IAchPushDisbursementData } from '../../../types/order/escrow/IAchPushDisbursementData';
import { IManualCheckDisbursementData } from '../../../types/order/escrow/IManualCheckDisbursementData';
import { IInternalTransferData } from '../../../types/order/escrow/IInternalTransferData';
import { OldTransactionsTable } from './disbursements/oldTransactions/OldTransactionsTable';
import { CheckDisbursementsTable } from './disbursements/checkDisbursements/CheckDisbursementsTable';

interface ISpaEscrowDisbursedTransactionsProps {
  orderId: string;
  tab?: OrderTabs;
}

type DisbursementUnion =
  | IEscrowWireDisbursementsData
  | IAchPushDisbursementData
  | IManualCheckDisbursementData
  | IInternalTransferData;

const filterDisbursements = <T extends DisbursementUnion>(disbursements: T[] = [], key: keyof T): T[] =>
  disbursements.filter((disbursement) => disbursement[key] !== null);

export const SpaEscrowDisbursedTransactionsContainer: React.FC<ISpaEscrowDisbursedTransactionsProps> = ({
  orderId,
  tab = 'Escrow',
}) => {
  const dispatch = useAppDispatch();
  const { data: escrowFileCompleteData, error } = useGetDetailedEscrowDataQuery(orderId);
  useEffect(() => {
    dispatch(orderNavigationSlice.actions.setup({ tab }));
    dispatch(loadEscrowBalancesAsync({ orderId }));
    if (escrowFileCompleteData) {
      dispatch(getEscrowFileAndVoidUsersAction(orderId, escrowFileCompleteData));
    }
  }, [dispatch, orderId, tab, escrowFileCompleteData]);

  const { data: order } = useKeyToSelect(orderId, (store: RootStore) => store.order.orderInformationSlice);

  const escrowFileCompleteDataFetchError = convertToError(error);
  // incoming
  const wireDepositData = escrowFileCompleteData?.wireDeposits || [];
  const checkDepositData = escrowFileCompleteData?.checkDeposits || [];
  // outgoing
  // note: outgoing transactions that are not marked as 'sent' are hidden
  const wireDisbursementsData = useMemo(
    () =>
      filterDisbursements<IEscrowWireDisbursementsData>(
        escrowFileCompleteData?.wireDisbursements,
        'enqueuedForDisbursementAt'
      ),
    [escrowFileCompleteData?.wireDisbursements]
  );
  const achPushDisbursements = useMemo(
    () => filterDisbursements<IAchPushDisbursementData>(escrowFileCompleteData?.achPushDisbursements, 'sentAt'),
    [escrowFileCompleteData?.achPushDisbursements]
  );
  const manualCheckDisbursementData = useMemo(
    () =>
      (escrowFileCompleteData?.manualCheckDisbursements || []).filter(
        (disbursement) => disbursement.sentAtDate !== null || disbursement.printedDate !== null
      ),
    [escrowFileCompleteData?.manualCheckDisbursements]
  );
  const restrictedInternalTransferData = useMemo(
    () =>
      filterDisbursements<IInternalTransferData>(
        escrowFileCompleteData?.restrictedInternalTransfers,
        'enqueuedForDisbursementAt'
      ),
    [escrowFileCompleteData?.restrictedInternalTransfers]
  );
  const unrestrictedInternalTransferData = useMemo(
    () =>
      filterDisbursements<IInternalTransferData>(
        escrowFileCompleteData?.unrestrictedInternalTransfers,
        'enqueuedForDisbursementAt'
      ),
    [escrowFileCompleteData?.unrestrictedInternalTransfers]
  );

  return (
    <>
      <Grid>
        <ErrorMessage error={escrowFileCompleteDataFetchError} />
        <Card title={'Disbursed Transactions'}>
          <Grid>
            <AchDepositsContainer escrowFileId={order?.transaction.escrow_file_id || ''} />
            <WireDepositsContainer order={order!} wireDeposits={wireDepositData} />
            <CheckDepositsContainer order={order!} checkDeposits={checkDepositData} />
            <EscrowWireDisbursementsContainer wireDisbursements={wireDisbursementsData} />
            <AchPushDisbursementsContainer achPushDisbursements={achPushDisbursements} />
            <ManualCheckDisbursementsTable manualCheckDisbursements={manualCheckDisbursementData} />
            <InternalTransfersContainer internalTransfers={restrictedInternalTransferData} />
            <UnrestrictedInternalTransfersContainer internalTransfers={unrestrictedInternalTransferData} />
            <EscrowFileJournalEntriesContainer escrowFileId={order?.transaction.escrow_file_id || ''} />
            <OldTransactionsTable oldTransactions={escrowFileCompleteData?.oldTransactions || []} />
            <CheckDisbursementsTable checkDisbursements={escrowFileCompleteData?.checkDisbursements || []} />
          </Grid>
        </Card>
      </Grid>
    </>
  );
};
